import React, { useState, useEffect } from "react";
import { SelectButton } from "primereact/selectbutton";
import { MoonIcon, SunIcon } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "hook/redux";
import { Themes } from "util/CommonConstant";
import { setMode } from "slice/CommonSlice";
import { setLocalStorage } from "util/CommonUtil";

const DarkAndLightModeSwitch = () => {
  const modeInRedux = useAppSelector((state) => state.common.mode);
  const reduxDispatch = useAppDispatch();

  const isDarkModeOnSystem =
    modeInRedux === Themes.DARK ||
    (modeInRedux !== Themes.LIGHT && window.matchMedia("(prefers-color-scheme: dark)").matches);

  const [theme, setTheme] = useState(isDarkModeOnSystem ? "dark" : "light");

  const handleModeChange = (mode: Themes) => {
    reduxDispatch(setMode(mode ?? Themes.SYSTEM));
    if (mode === Themes.DARK) {
      document.documentElement.classList.add("dark");
      setLocalStorage("theme", Themes.DARK);
    } else if (mode === Themes.LIGHT) {
      document.documentElement.classList.remove("dark");
      setLocalStorage("theme", Themes.LIGHT);
    }
  };

  // Initialize the theme on component mount
  useEffect(() => {
    const initialTheme = isDarkModeOnSystem ? Themes.DARK : Themes.LIGHT;
    setTheme(initialTheme === Themes.DARK ? "dark" : "light");
  }, [isDarkModeOnSystem]);

  const options = [
    {
      value: "light",
      icon: (
        <div className="flex h-6 w-6 items-center justify-center rounded-full">
          <SunIcon className="h-4.5 w-4.5" aria-hidden="true" />
        </div>
      ),
      label: "Light mode",
      "aria-label": "Switch to light mode",
    },
    {
      value: "dark",
      icon: (
        <div className="flex h-6 w-6 items-center justify-center rounded-full">
          <MoonIcon className="h-4.5 w-4.5" aria-hidden="true" />
        </div>
      ),
      label: "Dark mode",
      "aria-label": "Switch to dark mode",
    },
  ];

  const ToggleOptionTemplate = ({
    option,
  }: {
    option: { value: string; icon: JSX.Element; "aria-label": string };
  }) => (
    <div
      className="flex items-center rounded-full active:dark:bg-primary-electron-blue-400"
      role="button"
      aria-label={option["aria-label"]}
    >
      {option.icon}
    </div>
  );

  const handleOnChange = (e: { value: string }) => {
    if (e.value) {
      setTheme(e.value);
      handleModeChange(e.value === "dark" ? Themes.DARK : Themes.LIGHT);
    }
  };

  return (
    <div className="flex h-8.5 w-15 items-center justify-center rounded-2xl bg-light-grey-50 p-1 dark:bg-primary-electron-blue-300">
      <SelectButton
        id="theme-toggle-button"
        value={theme}
        options={options}
        onChange={handleOnChange}
        optionLabel="label"
        itemTemplate={(option) => <ToggleOptionTemplate option={option} />}
        className="dark_light_switch flex items-center"
      />
    </div>
  );
};

export default DarkAndLightModeSwitch;
